

import { z } from 'zod';
import { ObservacaoDoItemSchema } from '@/lib/orcamentos/observacaoDoItem';
import { OrcamentoStatusOptions, CancellationReasonOptions, PaymentMethodOptions, FinalidadeOptions } from './types';
import { cnpjValido, soDigitos } from './clientes/documento';

export { CancellationReasonOptions, PaymentMethodOptions };

export const FilamentSchema = z.object({
  id: z.string().optional(),
  tipoId: z.string().min(1, { message: "Tipo é obrigatório" }),
  cor: z.string().min(1, { message: "Cor é obrigatória" }),
  /** A cor exata (#RRGGBB), da paleta ou do seletor. Vazio = só o nome. */
  corHex: z.string().regex(/^#[0-9A-Fa-f]{6}$/, 'Cor inválida').optional().nullable().or(z.literal('').transform(() => null)),
  densidade: z.coerce.number().positive({ message: "Densidade deve ser positiva" }),
  marcaId: z.string().optional().nullable().transform(val => val === "" ? undefined : val),
  modelo: z.string().trim().optional().nullable().transform(val => val === "" ? undefined : val),
  temperaturaBicoIdeal: z.coerce.number().optional(),
  temperaturaMesaIdeal: z.coerce.number().optional(),
  precoPorKg: z.coerce.number().nonnegative({ message: "Preço por Kg não pode ser negativo" }).optional(),
  quantidadeEstoqueGramas: z.coerce.number().nonnegative({ message: "Quantidade em estoque não pode ser negativa" }).default(0).optional(),
  pesoRoloGramas: z.coerce.number().positive("O peso do carretel vazio deve ser positivo").optional().nullable(),
  ativo: z.boolean().default(true).optional(),
  ultimaConferenciaEm: z.coerce.date().optional().nullable(),
});

export const PrinterSchema = z.object({
  id: z.string().optional(),
  marcaId: z.string().min(1, { message: "A marca é obrigatória" }),
  modelo: z.string().trim().optional().nullable().transform(val => val === "" ? undefined : val),
  custoAquisicao: z.coerce.number().positive({ message: "Custo de aquisição deve ser um número positivo" }),
  // Opcional e ignorada ao gravar: a depreciação é calculada no servidor (PrinterService).
  taxaDepreciacaoHora: z.coerce.number().nonnegative({ message: "Taxa de depreciação não pode ser negativa" }).optional(),
  vidaUtilAnos: z.coerce.number().int({ message: "Vida útil deve ser um número inteiro" }).positive({ message: "Vida útil deve ser um número positivo" }),
  horasTrabalhoDia: z.coerce.number().int({ message: "Horas de trabalho por dia deve ser um número inteiro" }).positive({ message: "Horas de trabalho por dia deve ser um número positivo" }),
  funcionaFimSemana: z.boolean().default(true),
  ativo: z.boolean().default(true).optional(),
});

const ProdutoFilamentoSchema = z.object({
  filamentoId: z.string().min(1, { message: "Selecione um filamento" }),
  pesoGramas: z.coerce.number().positive({ message: "Peso deve ser positivo" }),
  // Só existem quando a linha veio de um G-code anexado. Produto
  // cadastrado à mão não tem placa nem coluna a que corresponder.
  slotGcode: z.coerce.number().int().min(1).max(16).nullable().optional(),
  placaIndice: z.coerce.number().int().min(1).max(64).nullable().optional(),
});

export const ProductFormSchema = z.object({
  id: z.string().optional(),
  nome: z.string().min(1, { message: "Nome é obrigatório" }),
  descricao: z.string().optional(),
  nichoId: z.string().optional().nullable().transform(val => val === "" ? undefined : val),
  grupoItemId: z.string().optional().nullable().transform(val => val === "" || val === "none" ? undefined : val),
  linkExterno: z.string().url({ message: "Por favor, insira uma URL válida." }).optional().nullable().transform(val => val === "" ? undefined : val),
  // Só dígitos; em branco vale o NCM padrão da empresa.
  ncm: z.string().optional().nullable().refine(v => !v || soDigitos(v).length === 8, { message: "O NCM tem 8 dígitos." }),
  dimensoes: z.object({
    largura: z.coerce.number().positive("Largura deve ser positiva.").optional().nullable(),
    profundidade: z.coerce.number().positive("Profundidade deve ser positiva.").optional().nullable(),
    altura: z.coerce.number().positive("Altura deve ser positiva.").optional().nullable(),
  }).optional(),
  // O teto é por PLACA, não por produto: o limite real é quantas cores a
  // caixa da impressora comporta numa impressão. Um produto de três mesas
  // pode legitimamente somar doze linhas — foi assim que o Bluey coube em
  // 6 cores num AMS de 4 posições.
  filamentos: z
    .array(ProdutoFilamentoSchema)
    .min(1, "Adicione pelo menos um filamento.")
    .max(64, "Máximo de 64 filamentos por produto.")
    .superRefine((linhas, ctx) => {
      const porPlaca = new Map<number, number>();
      for (const l of linhas) {
        const chave = l.placaIndice ?? 0;
        porPlaca.set(chave, (porPlaca.get(chave) ?? 0) + 1);
      }
      for (const [, quantas] of porPlaca) {
        if (quantas > 16) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Uma placa não pode ter mais de 16 cores.",
          });
          return;
        }
      }
    }),
  rendimentoPorCiclo: z.coerce.number().int().min(1, "No mínimo 1 unidade por ciclo").default(1),
  observacoesImpressao: z.string().max(2000, "No máximo 2000 caracteres").optional().or(z.literal("")),
  impressoraId: z.string().min(1, { message: "Impressora é obrigatória" }),
  tempoDias: z.coerce.number().int().min(0, "Dias não podem ser negativos").optional().default(0),
  tempoImpressaoHoras: z.coerce.number().int().min(0, "Horas não podem ser negativas").max(23, "Horas não podem ser maiores que 23").optional().default(0),
  tempoImpressaoMinutos: z.coerce.number().int().min(0, "Minutos não podem ser negativos").max(59, "Minutos não podem ser maiores que 59").optional().default(0),
  custoModelagem: z.coerce.number().nonnegative({ message: "Custo de modelagem não pode ser negativo" }).default(0),
  custosExtras: z.coerce.number().nonnegative({ message: "Custos extras não podem ser negativos" }).default(0),
  margemLucroPercentual: z.coerce.number().nonnegative({ message: "Margem de lucro não pode ser negativa" }).max(99.99, { message: "Margem de lucro deve ser menor que 100%" }).default(52.38),
  destaque: z.boolean().default(false).optional(),
  ativo: z.boolean().default(true).optional(),
  ecommerce: z.boolean().default(true).optional(),
  mostrarPrecoEcommerce: z.boolean().default(true).optional(),
  faz_estoque: z.boolean().default(false).optional(),
  regrasAtacado: z.array(z.object({
    minQty: z.coerce.number().int().min(1, "Quantidade mínima deve ser pelo menos 1"),
    discountPct: z.coerce.number().min(0, "Desconto não pode ser negativo").max(99.99, "Desconto deve ser menor que 100%"),
    label: z.string().optional(),
  })).optional(),
}).refine(data => (data.tempoDias || 0) + (data.tempoImpressaoHoras || 0) + (data.tempoImpressaoMinutos || 0) > 0, {
  message: "O tempo total de impressão deve ser maior que zero.",
  path: ["tempoDias"],
});

export const BrandSchema = z.object({
  id: z.string().optional(),
  nome_marca: z.string().min(1, { message: "Nome da marca é obrigatório" }).max(100, { message: "Nome da marca deve ter no máximo 100 caracteres" }),
  is_supplier: z.boolean().default(false).optional(),
  is_brand: z.boolean().default(true).optional(),
  ativo: z.boolean().default(true).optional(),
});

export const FilamentTypeSchema = z.object({
  id: z.string().optional(),
  tipo: z.string().min(1, { message: "Nome do tipo de filamento é obrigatório" }).max(50, { message: "Nome do tipo deve ter no máximo 50 caracteres" }),
  ativo: z.boolean().default(true).optional(),
});

export const NichoSchema = z.object({
  id: z.string().optional(),
  nome: z.string().min(2, { message: "O nome do nicho é obrigatório e deve ter no mínimo 2 caracteres." }).max(50, { message: "Nome do nicho deve ter no máximo 50 caracteres." }),
  ativo: z.boolean().default(true).optional(),
});

export const GrupoItemSchema = z.object({
  id: z.string().optional(),
  nome: z.string().min(2, { message: "O nome do grupo é obrigatório e deve ter no mínimo 2 caracteres." }).max(50, { message: "Nome do grupo deve ter no máximo 50 caracteres." }),
  ativo: z.boolean().default(true).optional(),
});

const MaterialSugeridoSchema = z.object({
  filamentoOriginalId: z.string().uuid(),
  filamentoId: z.string().uuid(),
  unidades: z.number().int().min(1),
});

export const OrcamentoItemSchema = z.object({
  id: z.string().uuid({ message: "ID do item inválido" }),
  produto_id: z.string().min(1, { message: "Produto é obrigatório" }),
  produtoNome: z.string().optional(),
  imageUrls: z.array(z.string()).optional(),
  quantidade: z.coerce.number().int().min(1, { message: "Quantidade deve ser no mínimo 1" }),
  preco_unitario_no_momento: z.coerce.number().nonnegative({ message: "Valor unitário não pode ser negativo" }),
  subtotal_item: z.coerce.number().nonnegative(),
  desconto_percentual: z.coerce.number().nonnegative("Desconto não pode ser negativo.").optional().default(0),
  /** Material sugerido para o item (só o que difere do cadastro do produto). */
  materiais: z.array(MaterialSugeridoSchema).optional(),
  /** Observação interna do item ("2 verde e 2 amarelo"). */
  observacao: ObservacaoDoItemSchema,
});

export const OrcamentoSchema = z.object({
  id: z.string().optional(),
  cliente_id: z.string().uuid().nullable().optional(),
  nome_cliente: z.string().min(1, { message: "Nome do cliente é obrigatório" }),
  contato_cliente: z.string().optional().nullable(),
  itens: z.array(OrcamentoItemSchema).min(1, { message: "Orçamento deve ter pelo menos um item" }),
  valor_total_orcamento: z.coerce.number().nonnegative().optional(),
  desconto_global_percentual: z.coerce.number().nonnegative("Desconto não pode ser negativo.").optional().default(0),
  despesas_adicionais: z.coerce.number().nonnegative("Despesas adicionais não podem ser negativas.").optional().default(0),
  ajuste_valor: z.coerce.number().optional().default(0),
  share_link: z.string().optional().nullable(),
});

export const OrcamentoActionItemSchema = z.object({
  produto_id: z.string(),
  quantidade: z.number().int().min(1),
  preco_unitario_no_momento: z.number().nonnegative(),
  desconto_percentual: z.number().nonnegative().optional().default(0),
  /**
   * Material sugerido. Ausente = o app/tela não mexeu nele e o que já estava
   * gravado se mantém (na edição); presente (mesmo vazio) = vale o enviado.
   */
  materiais: z.array(MaterialSugeridoSchema).optional(),
  /**
   * Observação interna do item. Ausente = a tela/o app não mexeu nela e a que já estava gravada se mantém
   * (na edição); presente (mesmo vazia) = vale a enviada.
   */
  observacao: ObservacaoDoItemSchema,
});

export const OrcamentoServerActionSchema = z.object({
  /** Cliente cadastrado; null = consumidor final. O servidor confere e põe o nome. */
  cliente_id: z.string().uuid().nullable().optional(),
  nome_cliente: z.string().min(1, { message: "Nome do cliente é obrigatório" }),
  contato_cliente: z.string().optional().nullable(),
  itens: z.array(OrcamentoActionItemSchema).min(1, { message: "Orçamento deve ter pelo menos um item" }),
  desconto_global_percentual: z.number().nonnegative().optional().default(0),
  despesas_adicionais: z.number().nonnegative().optional().default(0),
  ajuste_valor: z.number().optional().default(0),
});

export const EcommerceContactSchema = z.object({
  nome: z.string().min(2, { message: "Nome é obrigatório e deve ter no mínimo 2 caracteres." }),
  sobrenome: z.string().min(2, { message: "Sobrenome é obrigatório e deve ter no mínimo 2 caracteres." }),
  email: z.string().email({ message: "Por favor, insira um email válido." }),
  telefone: z.string().min(10, { message: "Telefone deve ter no mínimo 10 dígitos (com DDD)." }).regex(/^(\(?\d{2}\)?\s?)?(\d{4,5}-?\d{4})$/, { message: "Telefone deve conter apenas números." }),
  mensagem: z.string().min(10, { message: "Mensagem deve ter no mínimo 10 caracteres." }).max(1000, { message: "Mensagem deve ter no máximo 1000 caracteres." }),
});
export type EcommerceContactFormValues = z.infer<typeof EcommerceContactSchema>;

export const OrcamentoSolicitanteSchema = z.object({
  nomeCompleto: z.string().min(3, { message: "Nome completo é obrigatório (mínimo 3 caracteres)." }),
  email: z.string().email({ message: "Por favor, insira um email válido." }),
  telefone: z.string().min(10, { message: "Telefone deve ter no mínimo 10 dígitos (com DDD)." }).regex(/^(\(?\d{2}\)?\s?)?(\d{4,5}-?\d{4})$/, { message: "Formato de telefone inválido." }),
});
export type OrcamentoSolicitanteValues = z.infer<typeof OrcamentoSolicitanteSchema>;

export const AuthSchema = z.object({
  email: z.string().email({ message: "Por favor, insira um email válido." }),
  password: z.string()
    .min(6, { message: "A senha deve ter no mínimo 6 caracteres." })
    .regex(/[A-Z]/, { message: "A senha deve conter pelo menos uma letra maiúscula." })
    .regex(/[^a-zA-Z0-9]/, { message: "A senha deve conter pelo menos um caractere especial." }),
});
export type AuthFormValues = z.infer<typeof AuthSchema>;

export const UpdatePasswordSchema = z.object({
  password: AuthSchema.shape.password,
  confirmPassword: z.string(),
}).refine(data => data.password === data.confirmPassword, {
  message: "As senhas não coincidem.",
  path: ["confirmPassword"],
});
export type UpdatePasswordFormValues = z.infer<typeof UpdatePasswordSchema>;

export const OrcamentoFeedbackSchema = z.object({
  status: z.enum(["Aprovado", "Cancelado"]),
  cancellationReason: z.enum(CancellationReasonOptions).optional(),
  cancellationReasonText: z.string().max(255, { message: "O texto não pode exceder 255 caracteres." }).optional(),
  paymentMethod: z.enum(PaymentMethodOptions).optional(),
  /** Dia da entrega combinado ("AAAA-MM-DD"). Só quem usa o sistema vê o campo; a regra é do servidor (lib/pedidos/entrega). */
  dataEntrega: z.string().optional(),
}).refine(data => {
  if (data.status === 'Cancelado') {
    return !!data.cancellationReason;
  }
  return true;
}, {
  message: "O motivo do cancelamento é obrigatório.",
  path: ["cancellationReason"],
}).refine(data => {
  if (data.cancellationReason === 'Outros') {
    return !!data.cancellationReasonText && data.cancellationReasonText.trim().length > 0;
  }
  return true;
}, {
  message: "Por favor, especifique o motivo.",
  path: ["cancellationReasonText"],
}).refine(data => {
  if (data.status === 'Aprovado') {
    return !!data.paymentMethod;
  }
  return true
}, {
  message: "O método de pagamento é obrigatório.",
  path: ["paymentMethod"],
});

export type OrcamentoFeedbackFormValues = z.infer<typeof OrcamentoFeedbackSchema>;

export const ProductDbSchema = z.object({
  id: z.string().optional(),
  nome_produto: z.string(),
  descricao: z.string().optional().nullable(),
  image_urls: z.array(z.string()).optional().nullable(),
  impressora_id: z.string(),
  tempo_impressao_h: z.number(),
  custo_modelagem: z.number().optional().nullable(),
  custos_extras: z.number().optional().nullable(),
  percentual_lucro: z.number(),
  custo_total_calculado: z.number().optional().nullable(),
  preco_venda_calculado: z.number().optional().nullable(),
  peso_total_gramas: z.number().optional().nullable(),
});

export const ImpressaoAvulsaSchema = z.object({
  id: z.string().optional(),
  destinatario: z.string().min(1, { message: "O destinatário é obrigatório." }),
  finalidade: z.enum(FinalidadeOptions, { required_error: "A finalidade é obrigatória." }),
  produto_id: z.string().min(1, { message: "A seleção de um produto é obrigatória." }),
  quantidade: z.coerce.number().int().positive({ message: "A quantidade deve ser um número inteiro positivo." }).default(1),
});

/** Só dígitos; vazio vira null. */
const somenteDigitos = () => z.string().nullish().transform((v) => soDigitos(v) || null);
const textoOuNulo = (max: number) =>
  z.string().trim().max(max, `Até ${max} caracteres.`).nullish().transform((v) => (v ? v : null));

export const EmpresaSchema = z.object({
  razao_social: z.string().trim().min(3, "Razão Social é obrigatória."),
  fantasia: z.string().optional(),
  email: z.string().email("Email inválido.").optional().or(z.literal('')),
  telefone: z.string().optional(),
  whatsapp: z.string().optional(),
  estado: z.string().trim().toUpperCase().optional()
    .refine((v) => !v || /^[A-Z]{2}$/.test(v), 'UF com 2 letras.'),
  municipio: z.string().optional(),
  instagram: z.string().optional(),
  facebook: z.string().optional(),
  tiktok: z.string().optional(),
  youtube: z.string().optional(),
  // Dados fiscais: o que a nota fiscal pede do emitente.
  cnpj: somenteDigitos().refine((v) => v === null || cnpjValido(v), 'CNPJ inválido: confira os números.'),
  inscricao_estadual: somenteDigitos().refine((v) => v === null || (v.length >= 2 && v.length <= 14), 'Inscrição estadual: até 14 dígitos.'),
  inscricao_municipal: textoOuNulo(20),
  regime_tributario: z.enum(['mei', 'simples_nacional', 'regime_normal']).default('mei'),
  cep: somenteDigitos().refine((v) => v === null || v.length === 8, 'CEP tem 8 dígitos.'),
  logradouro: textoOuNulo(200),
  numero: textoOuNulo(20),
  complemento: textoOuNulo(120),
  bairro: textoOuNulo(120),
  ncm_padrao: somenteDigitos().refine((v) => v === null || v.length === 8, 'O NCM tem 8 dígitos.'),
});

export const DespesaSchema = z.object({
  id: z.string().optional(),
  descricao: z.string().min(1, { message: "A descrição é obrigatória" }),
  categoria: z.string().min(1, { message: "A categoria é obrigatória" }),
  valor: z.coerce.number().nonnegative({ message: "O valor não pode ser negativo" }),
  data_despesa: z.string().or(z.date()).transform(val => new Date(val).toISOString()),
  observacoes: z.string().optional().nullable().transform(val => val === "" ? undefined : val),
});

// --------------------------------------------------------------------
// FARM 3D — telemetria enviada pelo agente local
// --------------------------------------------------------------------
// Payload que o agente (Docker no NAS) posta em /api/farm/telemetry.
// Tudo é opcional exceto a chave de identificação da máquina, porque
// cada driver expõe um subconjunto diferente de campos.

export const FarmPrinterStatusSchema = z.enum([
  'printing',
  'idle',
  'paused',
  'finished',
  'offline',
  'error',
]);

/**
 * Uma posição do ACE/AMS como a MÁQUINA reporta.
 *
 * Chega junto da telemetria porque é lida no mesmo ciclo do driver. Não
 * substitui o vínculo manual slot→rolo: a caixa sabe que há PLA magenta na
 * posição 1, mas não sabe de qual rolo comprado ele veio.
 */
export const FarmSlotObservadoSchema = z.object({
  // 1-based, alinhado às colunas do cabeçalho do gcode. O agente converte
  // do 0-based da máquina antes de enviar.
  slot: z.coerce.number().int().min(1).max(16),
  tipo: z.string().max(40).nullable().optional(),
  cor_rgb: z.string().regex(/^#[0-9A-Fa-f]{6}$/).nullable().optional(),
  marca: z.string().max(80).nullable().optional(),
  material: z.string().max(120).nullable().optional(),
  percentual: z.coerce.number().min(0).max(100).nullable().optional(),
  status_bruto: z.coerce.number().int().nullable().optional(),
  porta_extensao: z.boolean().optional(),
  caixa: z.coerce.number().int().min(0).max(8).optional(),
});

export const FarmTelemetryPrinterSchema = z.object({
  // Identificação: pelo menos uma das três é obrigatória (validado abaixo).
  // `maquina_id` aponta para farm_maquinas (a MÁQUINA FÍSICA), não para
  // `impressoras`, que é o catálogo de modelos usado na precificação.
  maquina_id: z.string().uuid().optional(),
  serial_number: z.string().min(1).max(120).optional(),
  ip_address: z.string().min(3).max(60).optional(),

  driver_type: z.string().max(40).optional(),
  status_farm: FarmPrinterStatusSchema.optional(),
  /** Endereço do vídeo, montado pelo agente quando há túnel. */
  camera_url: z.string().url().max(600).nullable().optional(),
  /** Endereço do vídeo dentro da rede do estúdio (HTTP), para o app nativo. */
  camera_url_lan: z.string().url().max(600).nullable().optional(),

  job_nome: z.string().max(300).nullable().optional(),
  job_progresso: z.coerce.number().min(0).max(100).optional(),
  job_tempo_decorrido_s: z.coerce.number().int().min(0).optional(),
  job_tempo_restante_s: z.coerce.number().int().min(0).optional(),
  job_filamento_usado_g: z.coerce.number().min(0).optional(),

  temp_bico_atual: z.coerce.number().min(-50).max(700).optional(),
  temp_bico_alvo: z.coerce.number().min(0).max(700).optional(),
  temp_mesa_atual: z.coerce.number().min(-50).max(300).optional(),
  temp_mesa_alvo: z.coerce.number().min(0).max(300).optional(),

  fan_speed_pct: z.coerce.number().int().min(0).max(100).optional(),
  camada_atual: z.coerce.number().int().min(0).optional(),
  total_camadas: z.coerce.number().int().min(0).optional(),
  luz_ativa: z.boolean().optional(),

  // Caixa de cores (ACE/AMS). Ausente em impressora sem caixa.
  slots: z.array(FarmSlotObservadoSchema).max(16).optional(),
  slot_ativo: z.coerce.number().int().min(1).max(16).nullable().optional(),

  // Mapa {cor do arquivo: posição da caixa} e gramas previstas por cor, as
  // duas vindas da própria máquina. Chaves são índices 1-based em texto.
  mapa_cores: z.record(z.string(), z.coerce.number().int().min(1).max(16)).optional(),
  gramas_previstas_por_cor: z.record(z.string(), z.coerce.number().min(0)).optional(),
  caixa_umidade: z.coerce.number().min(0).max(100).nullable().optional(),
  caixa_secando: z.boolean().optional(),

  // Contadores CUMULATIVOS (nunca deltas) — o agente é a fonte de verdade.
  horas_impressao_totais: z.coerce.number().min(0).optional(),
  gramas_extrudadas_total: z.coerce.number().min(0).optional(),
  total_jobs_concluidos: z.coerce.number().int().min(0).optional(),

  firmware_versao: z.string().max(80).nullable().optional(),
  ultimo_erro: z.string().max(500).nullable().optional(),

  // Instante da leitura na impressora. Um lote drenado do buffer offline
  // pode chegar horas depois; sem isto o histórico e os jobs ficariam
  // carimbados na hora do reenvio.
  capturado_em: z.string().datetime().optional(),
}).refine(
  (p) => Boolean(p.maquina_id || p.serial_number || p.ip_address),
  { message: 'Informe maquina_id, serial_number ou ip_address para identificar a máquina.' }
);

export const FarmTelemetryBatchSchema = z.object({
  agente: z.object({
    nome: z.string().max(120).optional(),
    versao: z.string().max(40).optional(),
  }).optional(),
  // Lote: o agente envia as 10 máquinas de uma vez, não uma por request.
  printers: z.array(FarmTelemetryPrinterSchema).min(1).max(100),

  // Catálogo dos arquivos fatiados que o agente enxerga na pasta do NAS.
  // O conteúdo nunca sai de lá: o que sobe é a miniatura que o próprio
  // fatiador embutiu e umas vinte linhas de cabeçalho.
  arquivos: z
    .array(
      z.object({
        nome: z.string().min(1).max(300),
        tamanho: z.coerce.number().int().min(0).optional(),
        modificado_em: z.string().optional(),
        // PNG em base64. O teto de 512 KB é folga larga sobre os ~3 KB
        // reais — e impede que um arquivo estranho encha a linha.
        miniatura: z.string().max(512 * 1024).optional(),
        cabecalho: z.string().max(64 * 1024).optional(),
        assinatura: z.string().max(64).optional(),
      })
    )
    .max(500)
    .optional(),

  // Desfecho dos comandos que o agente recebeu no ciclo anterior.
  resultados: z
    .array(
      z.object({
        id: z.string().uuid(),
        ok: z.boolean(),
        mensagem: z.string().max(500).optional(),
        // Só em download: identificam o arquivo a liberar e a cópia
        // temporária a apagar da nuvem.
        arquivo: z.string().max(300).optional(),
        storagePath: z.string().max(500).optional(),
      })
    )
    .max(50)
    .optional(),
});

export type FarmTelemetryPrinterInput = z.infer<typeof FarmTelemetryPrinterSchema>;
export type FarmTelemetryBatchInput = z.infer<typeof FarmTelemetryBatchSchema>;

